/**
 * Plan of Record module — domain types.
 *
 * Aligns with supabase/migrations/419_plan_of_record_module.sql and the
 * assemble_por RPC in 420_plan_of_record_rpcs.sql.
 *
 * This module is a new, self-contained domain (tables prefixed `por_`). It is
 * deliberately NOT the same object as the canon `plan_of_record` table — see
 * docs/PLAN_OF_RECORD_DEVIATIONS.md for the full rationale.
 */

// ─── Enums (mirror the Postgres enums) ──────────────────────────────────────

export type PorStatus = 'draft' | 'in_review' | 'approved' | 'superseded';

/**
 * F2 Signal Framework condition — A (reference, includes deliberate hold);
 * B/C/D failure modes.
 *
 * SEMANTICS (canon: DOCS_V4/00-canon/25-signal-framework.v1.md §4, §6):
 * This is the PRESENT, observer/authority classification of the current course —
 * the "observer-now" lens, which can legitimately name a failure mode (B/C) live
 * because the observer sees a signal the actor may be missing. It is NOT the
 * retrospective "which condition was it, actually?" verdict (the actor-later
 * lens). That future-state verdict is a separate layer, deliberately deferred —
 * do not overload current_condition with it.
 *
 * See also priorities-ai-projects/visuals/F2-signal-framework.html.
 */
export type PorCondition = 'A' | 'B' | 'C' | 'D';

/** Display order on PoR signal surfaces. */
export const POR_CONDITION_ORDER: PorCondition[] = ['A', 'B', 'C', 'D'];

export type PorSectionType =
  | 'vision'
  | 'historical_context'
  | 'drivers'
  | 'operating_principles'
  | 'phases'
  | 'ppp'
  | 'change_roadmap'
  | 'org_view'
  | 'challenges'
  | 'appendices';

export type PorSectionStatus = 'draft' | 'in_review' | 'approved';
export type PorCommitmentType = 'hard' | 'soft' | 'aspirational';
export type PorCommitmentStatus = 'active' | 'next' | 'blocked' | 'complete' | 'watch';
export type PorKpiTier = 'primary' | 'health' | 'watch';
export type PorRiskSeverity = 'high' | 'medium' | 'low';
export type PorRiskStatus = 'open' | 'mitigated' | 'resolved';
export type PorAskDecisionType = 'approve' | 'resource' | 'authorize' | 'sequence' | 'confirm';
export type PorAskStatus = 'open' | 'approved' | 'deferred';

// ─── Row types ──────────────────────────────────────────────────────────────

export interface PorRecord {
  id: string;
  workspace_id: string;
  title: string;
  version: string;
  status: PorStatus;
  current_phase: number;
  total_phases: number;
  /** Present-state classification (observer/authority "now" lens). Not a
   *  retrospective verdict — see PorCondition doc and canon Doc 25 §6. */
  current_condition: PorCondition;
  condition_rationale: string;
  horizon_short: string | null;
  horizon_long: string | null;
  owner_id: string | null;
  executive_sponsor_id: string | null;
  plan_cycle_id: string | null;
  /** Workspace primary — at most one true per workspace; drives default entry. */
  is_primary: boolean;
  last_updated: string;
  next_review: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

/** Prefer primary, then approved, then most recently updated. */
export function sortPorsForDisplay(records: PorRecord[]): PorRecord[] {
  return [...records].sort((a, b) => {
    if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
    if (a.status === 'approved' && b.status !== 'approved') return -1;
    if (b.status === 'approved' && a.status !== 'approved') return 1;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
}

/** Resolve the workspace primary, or the best fallback when none is marked. */
export function resolvePrimaryPor(records: PorRecord[]): PorRecord | null {
  if (records.length === 0) return null;
  const primary = records.find((r) => r.is_primary);
  if (primary) return primary;
  return sortPorsForDisplay(records)[0] ?? null;
}

export interface PorSection {
  id: string;
  por_id: string;
  section_type: PorSectionType;
  title: string;
  body: string | null;
  sort_order: number;
  status: PorSectionStatus;
  updated_at: string;
  updated_by: string | null;
  created_at: string;
}

export interface PorCommitment {
  id: string;
  por_id: string;
  title: string;
  description: string | null;
  type: PorCommitmentType;
  status: PorCommitmentStatus;
  owner_ids: string[];
  target_window_days: number | null;
  target_window_label: string | null;
  blocker_description: string | null;
  linked_initiative_id: string | null;
  sort_order: number;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

export interface PorKpi {
  id: string;
  por_id: string;
  label: string;
  /** No data ≠ zero. When false the card renders "—", never "0". */
  has_data: boolean;
  value_raw: number | null;
  value_string: string | null;
  value_display: string | null;
  target_value_raw: number | null;
  target_label: string | null;
  target_window: string | null;
  progress_pct: number | null;
  tier: PorKpiTier;
  sort_order: number;
  updated_at: string;
  updated_by: string | null;
  created_at: string;
}

export interface PorSignalEvent {
  id: string;
  por_id: string;
  condition: PorCondition;
  description: string;
  rationale: string;
  action_required: string | null;
  linked_commitment_id: string | null;
  resolved_at: string | null;
  created_at: string;
  created_by: string | null;
}

export interface PorRisk {
  id: string;
  por_id: string;
  description: string;
  mitigation: string;
  severity: PorRiskSeverity;
  status: PorRiskStatus;
  owner_id: string | null;
  sort_order: number;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

export interface PorExecutiveAsk {
  id: string;
  por_id: string;
  rank: number;
  description: string;
  rationale: string;
  decision_type: PorAskDecisionType;
  status: PorAskStatus;
  due_by: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  created_by: string | null;
}

export interface PorProofPoint {
  id: string;
  por_id: string;
  value_display: string;
  label: string;
  context: string | null;
  /** false = aspirational / unvalidated. */
  confirmed: boolean;
  sort_order: number;
  created_at: string;
  created_by: string | null;
}

export interface PorGovernanceEvent {
  id: string;
  por_id: string;
  event_type: string;
  summary: string | null;
  previous_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  rationale: string;
  actor_id: string | null;
  created_at: string;
  /** Resolved by assemble_por for display. */
  actor_email?: string | null;
}

// ─── Assembled object (assemble_por RPC return shape) ───────────────────────

export interface AssembledPor {
  success: true;
  id: string;
  record: PorRecord & {
    owner_email: string | null;
    executive_sponsor_email: string | null;
  };
  sections: PorSection[];
  commitments: PorCommitment[];
  kpis: PorKpi[];
  signal_events: PorSignalEvent[];
  open_signal_events: PorSignalEvent[];
  risks: PorRisk[];
  executive_asks: PorExecutiveAsk[];
  proof_points: PorProofPoint[];
  governance_events: PorGovernanceEvent[];
  assembled_at: string;
}

export interface AssembleError {
  success: false;
  error: string;
}

export type AssemblePorResult = AssembledPor | AssembleError;

// ─── Input shapes ───────────────────────────────────────────────────────────

export interface CreatePorInput {
  title: string;
  horizon_short?: string | null;
  horizon_long?: string | null;
  owner_id?: string | null;
  executive_sponsor_id?: string | null;
  plan_cycle_id?: string | null;
}

// ─── Canonical reference data ───────────────────────────────────────────────

/** The 10 sections of Harry Max's outline, in canonical order. */
export const POR_SECTION_ORDER: PorSectionType[] = [
  'vision',
  'historical_context',
  'drivers',
  'operating_principles',
  'phases',
  'ppp',
  'change_roadmap',
  'org_view',
  'challenges',
  'appendices',
];

export const POR_SECTION_LABELS: Record<PorSectionType, string> = {
  vision: 'Vision Statement',
  historical_context: 'Historical Context',
  drivers: 'Drivers',
  operating_principles: 'Operating Principles',
  phases: 'Phases & Current Position',
  ppp: 'Progress, Plans, Priorities',
  change_roadmap: 'Change Roadmap',
  org_view: 'Org View',
  challenges: 'Current Challenges & Open Questions',
  appendices: 'Appendices',
};

/** One-line guidance shown as the empty-state prompt for each section. */
export const POR_SECTION_PROMPTS: Record<PorSectionType, string> = {
  vision: 'What we exist to make true. One paragraph max.',
  historical_context: 'How we got here — the backstory that makes the current plan legible.',
  drivers: 'Compelling reasons for change. Why this, why now. Be specific, not generic.',
  operating_principles:
    'Timeless philosophy of change. These do not move with the roadmap — they are the standards every decision is tested against.',
  phases: 'The macro arc. Which phase are we in now?',
  ppp: "Progress, Plans, Priorities — what's done, what's committed, what's ranked highest and lowest.",
  change_roadmap: 'Committed milestones with dates or windows — not a vague timeline.',
  org_view: 'Teams, roles, people. Simple — not a full org chart.',
  challenges: 'Live, honest open questions. Their quality is a signal of organizational health.',
  appendices: 'Details by topic — linked, not embedded. Deep technical and operational content lives here.',
};

/**
 * F2 Signal Framework condition metadata (canon 23 §6 v2, ratified 2026-08-20).
 * A/B/C/D are RETROSPECTIVE verdict grammar and derived-exposure vocabulary —
 * names and definitions only. Never render these as a live/present-tense
 * picker: capture uses occurrence + routing class (canon 23 §6.1), letters are
 * computed states of the record or reflection-time verdicts (§6.3/§6.5).
 */
export interface PorConditionMeta {
  code: PorCondition;
  name: string;
  /** A is the healthy reference case; B/C/D are failure modes. */
  isFailureMode: boolean;
  definition: string;
  behavioralImplication: string;
}

export const POR_CONDITIONS: Record<PorCondition, PorConditionMeta> = {
  A: {
    code: 'A',
    name: 'Signal-Informed Adjustment',
    isFailureMode: false,
    definition:
      'A course change — minor or major — directly traceable to a confirmed signal from within or outside the organization.',
    behavioralImplication:
      'The organization changes, and documents why. The Plan of Record is updated; the prior commitment is closed with a record of what changed and what caused it. This is the system working.',
  },
  B: {
    code: 'B',
    name: 'Signal Not Acted On',
    isFailureMode: true,
    definition:
      'A confirmed signal exists — visible, acknowledged, unambiguous — and the organization holds course anyway.',
    behavioralImplication:
      'Holding is no longer discipline; it is avoidance. Act: update the Plan of Record, reallocate, and name what is being stopped and why.',
  },
  C: {
    code: 'C',
    name: 'Course Change Without Signal',
    isFailureMode: true,
    definition:
      'A reprioritization occurs with no instrument input — no new information, no confirmed change in conditions, no traceable cause.',
    behavioralImplication:
      'The proposed change should not proceed. The Plan of Record holds. Ask again what specifically changed; if no signal can be named, commitments stay intact.',
  },
  D: {
    code: 'D',
    name: 'Change Before Signal',
    isFailureMode: true,
    definition:
      'A course change made while the instruments are still resolving — on anticipation rather than confirmed evidence.',
    behavioralImplication:
      'Hold. The Plan of Record holds until the signal resolves. Prepare contingency thinking only — do not move resources or revise commitments.',
  },
};
